// =========================================================================
// IssuesLiveScene — "what is wrong right now". Fork-owned (zone C).
//
// Port of `renderIssues()` and its helpers (`issueHero`, `alertCard`,
// `ticker`, `siteHeat`, `incidentStrip`, `watchTable`) at lines 3096–3186 of
// `/Users/timwark/Projects/Data/OEM+/OEM+_Platform_Demo.html`.
//
// The executive hook is the second hero tile: what the seven open alerts cost
// if nobody acts on them, priced unplanned against planned from the site rate
// card. The demo sent that figure to a tab; here the figure itself opens its
// own working in the `oem-source` drawer, so the headline number is auditable
// in one click.
//
// Four tabs, carried in `?tab=` so a deep link survives a reload:
//   now        hero · critical alerts · sites + ticker · incidents · watch
//   alerts     hero · every alert · the cost-of-waiting table
//   incidents  the incident strip · the standing rules each one produced
//   watch      the full watch list · the rules the fleet now runs on
//
// UI layer. Every figure comes from `shared/oem-engine.ts`; arrangement,
// keys and tab parsing come from the sibling transform.
//
// @see docs/logic/issues/live.md
// =========================================================================

import { useMemo, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import {
  EmptyState,
  HelpAnchor,
  Kicker,
  SkeletonBlock,
  SurfacePageBody,
  SurfacePageLayout,
  SurfacePageTabs,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '@/versions/v1/chrome-oem/AskStrip';
import { cn } from '@/shared/cn';
import { unpackTable } from '@/versions/v1/shared/columnar';
import {
  alertCardData,
  fmt$,
  groupNum,
  incidentStrip,
  issueCounts,
  issueHero,
  ISSUE_TABS,
  siteHeat,
  ticker,
  watchTable,
  WATCH_TABLE_LIMIT,
  type IssueTab,
} from '@/versions/v1/shared/oem-engine';
import { INCIDENTS } from '@/versions/v1/shared/seed/incidents';
import { useOemData, type WorkOrder } from '@/versions/v1/shared/useOemData';
import { IssueMachinePicture } from '@/versions/v1/shared/viz/IssueMachinePicture';
import { AlertCard } from './AlertCard';
import { RoutingLinkCard, useAlertCauses } from './RouteCards';
import { actionHref } from './action-href';
import {
  bandLegend,
  coiDrawerPayload,
  coiTableRows,
  fleetRuleRows,
  heatSegments,
  parseIssueTab,
  splitAlertsBySeverity,
  tickerKey,
  watchRowKey,
} from './IssuesLiveScene.transform';
import { useSourceDrawer } from './useSourceDrawer';
import { useDrawer } from '@/app/providers';

/** The demo's `as of` line (line 3165) — the frozen OEM clock, not `Date.now()`. */
const AS_OF = 'as of 09 Sep 2026 06:05';

/**
 * How many watch rows the `now` tab shows (simplification pass B8).
 *
 * The dedicated `watch` tab still slices the engine's `WATCH_TABLE_LIMIT`,
 * because that tab *is* the watch list. On `now` the table is one of five
 * sections and its 28 rows were most of the page's 5-viewport depth; eight
 * rows plus the count says the same thing, and the section's own "the full
 * watch list ›" link is one click from the rest.
 */
const WATCH_PREVIEW_LIMIT = 8;

/** One sentence (simplification pass B4). The four tabs below say the rest. */
const DESCRIPTION =
  'Every open alert with the time it has left and what waiting costs, raised by the agents from the live feeds and the shift-end exports.';

export function IssuesLiveScene() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseIssueTab(searchParams.get('tab'));

  // Four domains: work orders (drafts, incident costs), service (technician
  // jobs, cases, remote sessions), parts (requisitions, orders) and the RMS
  // analyst feed. The ticker and the job board read across all four.
  const workOrdersData = useOemData('work-orders');
  const serviceData = useOemData('service');
  const partsData = useOemData('parts');
  const rmsData = useOemData('rms');

  const loading =
    workOrdersData.loading || serviceData.loading || partsData.loading || rmsData.loading;
  const error =
    workOrdersData.error ?? serviceData.error ?? partsData.error ?? rmsData.error ?? null;

  const workOrders = useMemo<readonly WorkOrder[]>(
    () => (workOrdersData.data ? unpackTable<WorkOrder>(workOrdersData.data.wos) : []),
    [workOrdersData.data],
  );

  const ctx = useMemo(
    () => ({
      workOrders,
      techJobs: serviceData.data?.tapp_jobs ?? [],
      cases: serviceData.data?.cases ?? [],
      remoteSessions: serviceData.data?.xreach_sessions ?? [],
      orders: partsData.data?.orders ?? [],
      rmsAlerts: rmsData.data?.rms_alerts ?? [],
    }),
    [workOrders, serviceData.data, partsData.data, rmsData.data],
  );

  const ready = !loading && !error && Boolean(workOrdersData.data);

  const counts = useMemo(() => issueCounts(), []);
  const split = useMemo(() => splitAlertsBySeverity(), []);
  const heat = useMemo(() => siteHeat(), []);
  const watch = useMemo(() => watchTable(), []);
  const legend = useMemo(() => bandLegend(), []);
  const rules = useMemo(() => fleetRuleRows(), []);

  const hero = useMemo(() => (ready ? issueHero(ctx) : null), [ready, ctx]);
  const events = useMemo(() => (ready ? ticker(ctx) : []), [ready, ctx]);
  const incidents = useMemo(() => (ready ? incidentStrip(ctx) : []), [ready, ctx]);
  const cards = useMemo(
    () => (ready ? split.all.map((alert) => alertCardData(alert, ctx)) : []),
    [ready, split.all, ctx],
  );
  const cardById = useMemo(
    () => new Map(cards.map((card) => [card.alert.id, card])),
    [cards],
  );

  const setTab = (next: IssueTab) => {
    setSearchParams(next === 'now' ? {} : { tab: next }, { replace: false });
  };

  const tabItems = ISSUE_TABS.map(([id, label]) => {
    const badge =
      id === 'alerts'
        ? counts.alerts
        : id === 'incidents'
          ? counts.incidents
          : id === 'watch'
            ? counts.watchList
            : null;
    return { id: id as IssueTab, label: badge == null ? label : `${label} ${badge}` };
  });

  return (
    <SurfacePageLayout width="full">
      <Commentable name="IssuesLiveScene">
        <div className="contents">
          <SurfacePageBody className="space-y-4" as="div">
            <Commentable name="IssuesHeader">
              <header className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0">
                    <h1 className="text-2xl font-semibold tracking-tight text-[var(--ink-primary)]">
                      Live{' '}
                      <span className="text-sm font-normal text-[var(--ink-tertiary)]">
                        · what is wrong right now
                      </span>
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--ink-secondary)]">
                      {DESCRIPTION}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1.5">
                    <span
                      className="rounded-full px-2.5 py-1 text-xs font-medium"
                      style={{ background: 'var(--vpoor-soft)', color: 'var(--vpoor)' }}
                    >
                      {counts.critical} critical · {counts.watch} watch · {counts.incidents}{' '}
                      incidents
                    </span>
                    <span className="text-xs text-[var(--ink-tertiary)]">{AS_OF}</span>
                    <HelpAnchor doc="issues/live" />
                  </div>
                </div>

                <SurfacePageTabs
                  items={tabItems}
                  value={tab}
                  onChange={setTab}
                  aria-label="Issues view"
                />
              </header>
            </Commentable>

            {/*
              B3: the ask chips belong under the first numbers. On `now` and
              `alerts` that is the hero tile row inside the branch; on
              `incidents` and `watch` there is no tile row, so the strip stays
              directly under the tabs.
            */}
            {tab === 'now' || tab === 'alerts' ? null : <AskStrip view="issues" />}

            {error ? (
              <EmptyState
                icon={<AlertTriangle className="h-8 w-8 text-[var(--crit)]" />}
                title="The fleet data did not load"
                description={error.message}
              />
            ) : null}

            {tab === 'now' ? (
              <>
                <HeroTiles hero={hero} />
                <AskStrip view="issues" />
                <Section
                  title="Critical — acting today"
                  subtitle="the machine, the window, the cost of waiting and who it is waiting on"
                >
                  <AlertCards
                    ready={ready}
                    cards={split.critical
                      .map((a) => cardById.get(a.id))
                      .filter((c): c is NonNullable<typeof c> => Boolean(c))}
                  />
                </Section>

                {/* fork(oem/v1.8): the way in to root cause, routing and training. */}
                <Commentable name="RoutingLink">
                  <div>
                    <RoutingLinkCard />
                  </div>
                </Commentable>

                <div className="grid gap-4 lg:grid-cols-2">
                  <Commentable name="SiteHeat">
                    <section className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
                      <Kicker className="mb-2 block">
                        Sites · condition bands and what is open
                      </Kicker>
                      <SiteHeatTable rows={heat} />
                      <div className="mt-3 flex flex-wrap gap-3 border-t border-[var(--hairline)] pt-3 text-[11px] text-[var(--ink-tertiary)]">
                        {legend.map((band) => (
                          <span key={band.key} className="inline-flex items-center gap-1.5">
                            <i
                              aria-hidden
                              className="inline-block h-2 w-2 rounded-sm"
                              style={{ background: band.colorToken }}
                            />
                            {band.label}
                          </span>
                        ))}
                      </div>
                    </section>
                  </Commentable>

                  <Commentable name="EventTicker">
                    <section className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
                      <Kicker className="mb-2 block">Last 24 hours</Kicker>
                      <Ticker ready={ready} events={events} />
                    </section>
                  </Commentable>
                </div>

                <Section
                  title="Incidents this fortnight"
                  subtitle="what each one cost, against what the same fortnight cost when it is replayed through the agent chain on the data that already existed · open one for the replay"
                >
                  <IncidentStrip ready={ready} cards={incidents} />
                </Section>

                <Section
                  title="On watch — not yet an alert"
                  subtitle={`${counts.watchList} components in an attention band without an open alert`}
                  action={
                    <button
                      type="button"
                      className="text-xs font-medium text-[var(--accent)] hover:underline"
                      onClick={() => setTab('watch')}
                    >
                      the full watch list ›
                    </button>
                  }
                >
                  <WatchTable rows={watch} limit={WATCH_PREVIEW_LIMIT} />
                </Section>
              </>
            ) : null}

            {tab === 'alerts' ? (
              <>
                <HeroTiles hero={hero} />
                <AskStrip view="issues" />
                <Section
                  title="Every open alert"
                  subtitle="critical first · each priced planned against unplanned from the site rate card"
                >
                  <AlertCards ready={ready} cards={cards} />
                </Section>
                <CoiTable />
              </>
            ) : null}

            {tab === 'incidents' ? (
              <>
                <Section
                  title="Incidents this fortnight"
                  subtitle="each one twice: what happened, and the same days replayed through the agent chain on the data that already existed"
                >
                  <IncidentStrip ready={ready} cards={incidents} />
                </Section>
                <RulesTable
                  title="What each incident changed for the whole fleet"
                  rows={rules}
                  footer={
                    <>
                      Open an incident for the full timeline, the chart of the precursor and the
                      replay.{' '}
                      <Link
                        to="/issues/incidents"
                        className="font-medium text-[var(--accent)] hover:underline"
                      >
                        Incidents ›
                      </Link>
                    </>
                  }
                />
              </>
            ) : null}

            {tab === 'watch' ? (
              <>
                <Section
                  title="Watch list · every component below 60/100 without an open alert"
                  subtitle="the early warnings — each has a job on the board at the Demand sensing module"
                >
                  <WatchTable rows={watch} limit={WATCH_TABLE_LIMIT} />
                </Section>
                <RulesTable title="Watch conditions the fleet now runs on" rows={rules} />
              </>
            ) : null}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

function HeroTiles({ hero }: { hero: ReturnType<typeof issueHero> | null }) {
  const { open } = useDrawer();

  if (!hero) {
    return (
      <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <SkeletonBlock key={i} className="h-[104px] rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <Commentable name="IssueHero">
      <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        <HeroTile
          accent="var(--vpoor)"
          figureColor="var(--vpoor)"
          wash
          pulse
          figure={String(hero.criticalAlerts)}
          label="critical alerts open"
          detail={`${hero.newToday} raised since yesterday · ${hero.liveAlerts} caught live inside the shift`}
        />
        <HeroTile
          accent="var(--poor)"
          figureColor="var(--crit)"
          figure={hero.exposureLabel}
          label="cost of doing nothing"
          detail="the seven open alerts, priced unplanned against planned"
          action={{
            label: 'how it is worked out',
            onClick: () =>
              open('oem-source', {
                wide: true,
                title: 'Cost of doing nothing · per open alert',
                data: coiDrawerPayload(),
              }),
          }}
        />
        <HeroTile
          accent="var(--info)"
          figure={String(hero.waiting)}
          label="waiting on a person"
          detail={`${hero.draftedWorkOrders} drafted work orders, ${hero.draftRequisitions} requisitions, 1 restriction`}
          link={{ label: 'approvals', to: '/approvals' }}
        />
        <HeroTile
          accent="var(--fair)"
          figure={String(hero.veryPoorOrWorse)}
          label="machines very poor or worse"
          detail={`${hero.restrictions} restrictions and holds in force · ${hero.watchComponents} components on the watch list`}
        />
      </div>
    </Commentable>
  );
}

function HeroTile({
  accent,
  figureColor,
  figure,
  label,
  detail,
  wash = false,
  pulse = false,
  action,
  link,
}: {
  accent: string;
  figureColor?: string;
  figure: string;
  label: string;
  detail: string;
  wash?: boolean;
  pulse?: boolean;
  action?: { label: string; onClick: () => void };
  link?: { label: string; to: string };
}) {
  return (
    <div
      className="flex items-start gap-2.5 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4"
      style={{
        borderLeft: `4px solid ${accent}`,
        background: wash
          ? 'linear-gradient(100deg, var(--crit-soft), var(--surface-1) 62%)'
          : undefined,
      }}
    >
      {pulse ? (
        <span aria-hidden className="relative mt-2 h-2.5 w-2.5 flex-none">
          <i className="absolute inset-0 rounded-full bg-[var(--vpoor)]" />
          <i className="absolute -inset-1 animate-ping rounded-full border-2 border-[var(--vpoor)] opacity-60" />
        </span>
      ) : null}
      <div className="min-w-0">
        <div
          className="mono text-[27px] font-semibold leading-none"
          style={{ color: figureColor ?? 'var(--ink-primary)' }}
        >
          {figure}
        </div>
        <div className="mt-1 text-xs font-medium text-[var(--ink-primary)]">{label}</div>
        <div className="mt-1 text-[10.5px] leading-relaxed text-[var(--ink-tertiary)]">
          {detail}
          {action ? (
            <>
              {' · '}
              <button
                type="button"
                onClick={action.onClick}
                className="font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
              >
                {action.label}
              </button>
            </>
          ) : null}
          {link ? (
            <>
              {' · '}
              <Link
                to={link.to}
                className="font-medium text-[var(--accent)] hover:underline"
              >
                {link.label}
              </Link>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function Section({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-2 pt-2">
      <div className="flex flex-wrap items-baseline gap-2.5">
        <h2 className="text-sm font-semibold text-[var(--ink-primary)]">{title}</h2>
        {subtitle ? (
          <span className="min-w-0 text-[11.5px] text-[var(--ink-tertiary)]">{subtitle}</span>
        ) : null}
        {action ? <span className="ml-auto">{action}</span> : null}
      </div>
      {children}
    </section>
  );
}

function AlertCards({
  ready,
  cards,
}: {
  ready: boolean;
  cards: ReturnType<typeof alertCardData>[];
}) {
  // fork(oem/v1.8): operator / equipment / mixed pill per card.
  const causes = useAlertCauses();
  if (!ready) {
    return (
      <div className="grid gap-2.5 2xl:grid-cols-2">
        {[0, 1].map((i) => (
          <SkeletonBlock key={i} className="h-[280px] rounded-xl" />
        ))}
      </div>
    );
  }
  return (
    <Commentable name="AlertCards">
      <div className="grid gap-2.5 2xl:grid-cols-2">
        {cards.map((card) => (
          <AlertCard key={card.alert.id} data={card} cause={causes?.get(card.alert.id) ?? null} />
        ))}
      </div>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// Site heat
// ---------------------------------------------------------------------------

function SiteHeatTable({ rows }: { rows: ReturnType<typeof siteHeat> }) {
  return (
    <div className="divide-y divide-[var(--hairline)]">
      {rows.map((row) => {
        const href = actionHref(row.action);
        const segments = heatSegments(row);
        const body = (
          <>
            <span className="min-w-0 text-xs font-medium leading-tight text-[var(--ink-primary)]">
              {row.name}
              <span className="block text-[10.5px] font-normal text-[var(--ink-tertiary)]">
                {row.state} · {row.machines}
              </span>
            </span>
            <span className="flex h-[9px] overflow-hidden rounded-full bg-[var(--hairline)]">
              {segments.map((segment) => (
                <i
                  key={segment.band.key}
                  title={`${segment.count} ${segment.band.label.toLowerCase()}`}
                  style={{ flex: segment.count, background: segment.band.colorToken }}
                />
              ))}
            </span>
            <span className="flex flex-wrap justify-end gap-1">
              {row.alerts ? (
                <span
                  className="rounded-full px-2 py-0.5 text-[10.5px] font-medium"
                  style={{ background: 'var(--vpoor-soft)', color: 'var(--vpoor)' }}
                >
                  {row.alerts} alert{row.alerts > 1 ? 's' : ''}
                </span>
              ) : (
                <span className="text-[10.5px] text-[var(--ink-tertiary)]">no alerts</span>
              )}
              {row.incidents ? (
                <span className="rounded-full bg-[var(--surface-2)] px-2 py-0.5 text-[10.5px] text-[var(--ink-secondary)]">
                  {row.incidents} incident
                </span>
              ) : null}
            </span>
          </>
        );
        const gridClass =
          'grid grid-cols-[minmax(110px,1.1fr)_minmax(80px,1.6fr)_auto] items-center gap-2.5 px-1 py-1.5 text-left';
        return href ? (
          <Link
            key={row.mine}
            to={href}
            className={cn(gridClass, 'transition-colors hover:bg-[var(--surface-2)]')}
          >
            {body}
          </Link>
        ) : (
          <div key={row.mine} className={gridClass}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ticker
// ---------------------------------------------------------------------------

const TICKER_TONE: Record<string, { bg: string; fg: string }> = {
  crit: { bg: 'var(--crit-soft)', fg: 'var(--crit)' },
  warn: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
  good: { bg: 'var(--good-soft)', fg: 'var(--good)' },
  info: { bg: 'var(--info-soft)', fg: 'var(--info)' },
};

function Ticker({
  ready,
  events,
}: {
  ready: boolean;
  events: ReturnType<typeof ticker>;
}) {
  if (!ready) return <SkeletonBlock className="h-[330px] rounded-lg" />;

  return (
    <div className="scrollbar-subtle max-h-[330px] divide-y divide-[var(--hairline)] overflow-y-auto">
      {events.map((event, index) => {
        const tone = TICKER_TONE[event.sev] ?? TICKER_TONE.info;
        // The `focus` field is the demo's scroll-to-card id; on the Alerts tab
        // the same card carries `id="ic-<alertId>"`, so a hash lands on it.
        const href = actionHref(event.action);
        const to = event.focus && href ? `${href}?tab=alerts#${event.focus}` : href;
        const body = (
          <>
            <time className="mono text-[10.5px] text-[var(--ink-tertiary)]">{event.t}</time>
            <span
              className="justify-self-start rounded px-1.5 py-0.5 text-[9.5px] uppercase tracking-[0.04em]"
              style={{ background: tone.bg, color: tone.fg }}
            >
              {event.k}
            </span>
            <span className="text-[11.5px] leading-snug text-[var(--ink-secondary)]">
              {event.txt}
            </span>
          </>
        );
        const gridClass =
          'grid grid-cols-[78px_80px_minmax(0,1fr)] items-baseline gap-2 px-1 py-1.5 text-left';
        return to ? (
          <Link
            key={tickerKey(event, index)}
            to={to}
            className={cn(gridClass, 'transition-colors hover:bg-[var(--surface-2)]')}
          >
            {body}
          </Link>
        ) : (
          <div key={tickerKey(event, index)} className={gridClass}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Incident strip
// ---------------------------------------------------------------------------

function IncidentStrip({
  ready,
  cards,
}: {
  ready: boolean;
  cards: ReturnType<typeof incidentStrip>;
}) {
  if (!ready) {
    return (
      <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        {INCIDENTS.map((incident) => (
          <SkeletonBlock key={incident.id} className="h-[200px] rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <Commentable name="IncidentStrip">
      <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const href = actionHref(card.action);
          const machineHref = actionHref(card.openMachine);
          return (
            <article
              key={card.incident.id}
              className="min-w-0 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4 transition-shadow hover:shadow-md"
            >
              <div className="mb-1.5 flex items-center gap-2">
                <span className="mono rounded bg-[var(--surface-2)] px-1.5 py-0.5 text-[10.5px] text-[var(--ink-secondary)]">
                  {card.incident.id}
                </span>
                <span className="text-[10.5px] text-[var(--ink-tertiary)]">
                  {card.incident.date}
                </span>
              </div>
              {href ? (
                <Link
                  to={href}
                  className="mb-1.5 block text-[13px] font-semibold leading-snug text-[var(--ink-primary)] hover:text-[var(--accent)]"
                >
                  {card.incident.title}
                </Link>
              ) : (
                <b className="mb-1.5 block text-[13px] leading-snug">{card.incident.title}</b>
              )}
              {/* demo `.mini.go` (line 3157): the still, captioned, one link to the machine */}
              {machineHref ? (
                <Link
                  to={machineHref}
                  title={`Open ${card.incident.machine} — equipment details`}
                  className="block rounded-lg p-1 transition-shadow hover:shadow-[0_0_0_2px_var(--accent-soft)]"
                >
                  <IssueMachinePicture
                    machineId={card.incident.machine}
                    highlight={card.incident.comp}
                    size="compact"
                  />
                  <span className="mono mt-0.5 block text-center text-[10.5px] text-[var(--accent)]">
                    {card.incident.machine} · {card.machine.model} ›
                  </span>
                </Link>
              ) : (
                <IssueMachinePicture
                  machineId={card.incident.machine}
                  highlight={card.incident.comp}
                  size="compact"
                />
              )}

              <div className="my-2 space-y-1">
                <CompareRow
                  label="As it happened"
                  pct={card.actualPct}
                  value={fmt$(card.actual)}
                  color="var(--crit)"
                />
                <CompareRow
                  label="Replayed with the agents"
                  pct={card.plannedPct}
                  value={fmt$(card.planned)}
                  color="var(--good)"
                />
              </div>

              <p className="border-t border-[var(--hairline)] pt-1.5 text-[11px] text-[var(--ink-secondary)]">
                <b className="font-semibold">{card.warningDays} days</b> of warning already in the
                data · <span className="text-[var(--ink-tertiary)]">{card.attribution}</span>
              </p>
              <p className="mt-1 text-[11px] text-[var(--ink-tertiary)]">
                Rule now running: {card.rule}
              </p>
            </article>
          );
        })}
      </div>
    </Commentable>
  );
}

function CompareRow({
  label,
  pct,
  value,
  color,
}: {
  label: string;
  pct: number;
  value: string;
  color: string;
}) {
  return (
    <div className="grid grid-cols-[110px_minmax(0,1fr)_62px] items-center gap-2 text-[10.5px]">
      <span className="text-[var(--ink-tertiary)]">{label}</span>
      <span className="h-2 overflow-hidden rounded bg-[var(--hairline)]">
        <i className="block h-full" style={{ width: `${pct}%`, background: color }} />
      </span>
      <span className="mono text-right font-semibold text-[var(--ink-primary)]">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Watch table
// ---------------------------------------------------------------------------

function WatchTable({
  rows,
  limit,
}: {
  rows: ReturnType<typeof watchTable>;
  limit: number;
}) {
  const { openDataset } = useSourceDrawer();
  const shown = rows.slice(0, limit);

  return (
    <Commentable name="WatchTable">
      <div className="rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
        <div className="scrollbar-subtle overflow-x-auto">
          <table className="w-full min-w-[54rem] border-collapse text-[11.5px]">
            <thead>
              <tr className="border-b border-[var(--hairline)] text-left text-[10.5px] uppercase tracking-wide text-[var(--ink-tertiary)]">
                <th className="px-2 py-2 font-medium">Machine</th>
                <th className="px-2 py-2 font-medium">Site</th>
                <th className="px-2 py-2 font-medium">Component</th>
                <th className="px-2 py-2 text-right font-medium">Condition</th>
                <th className="px-2 py-2 text-right font-medium">Since change</th>
                <th className="px-2 py-2 text-right font-medium">Remaining</th>
                <th className="px-2 py-2 font-medium">What the register says</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((row) => {
                const href = actionHref(row.action);
                return (
                  <tr
                    key={watchRowKey(row)}
                    className="border-b border-[var(--hairline)] last:border-0 hover:bg-[var(--surface-2)]"
                  >
                    <td className="px-2 py-1.5">
                      {href ? (
                        <Link
                          to={href}
                          className="mono font-medium text-[var(--accent)] hover:underline"
                        >
                          {row.machine_id}
                        </Link>
                      ) : (
                        <span className="mono">{row.machine_id}</span>
                      )}{' '}
                      <span className="text-[var(--ink-tertiary)]">{row.model}</span>
                    </td>
                    <td className="px-2 py-1.5 text-[var(--ink-secondary)]">{row.site}</td>
                    <td className="px-2 py-1.5 text-[var(--ink-secondary)]">{row.component}</td>
                    <td className="px-2 py-1.5 text-right">
                      <span className={`band-pill band-${row.band} mono`}>
                        {row.health_score}/100
                      </span>
                    </td>
                    <td className="mono px-2 py-1.5 text-right text-[var(--ink-secondary)]">
                      {groupNum(row.hours_since_change)} h
                    </td>
                    <td
                      className="mono px-2 py-1.5 text-right"
                      style={{ color: row.remaining < 0 ? 'var(--crit)' : undefined }}
                    >
                      {row.remaining < 0
                        ? `overdue ${groupNum(-row.remaining)}`
                        : groupNum(row.remaining)}{' '}
                      h
                    </td>
                    <td className="max-w-[44ch] px-2 py-1.5 text-[var(--ink-tertiary)]">
                      {row.note ?? '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {rows.length > limit ? (
          <p className="mt-2 text-[11px] text-[var(--ink-tertiary)]">
            Showing {limit} of {rows.length} ·{' '}
            <button
              type="button"
              className="font-medium text-[var(--accent)] underline decoration-dotted underline-offset-2 hover:no-underline"
              onClick={() => openDataset('health', { title: 'Full condition register' })}
            >
              full condition register
            </button>
          </p>
        ) : null}
      </div>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// Cost-of-waiting table
// ---------------------------------------------------------------------------

function CoiTable() {
  const rows = useMemo(() => coiTableRows(), []);
  const total = rows.reduce((sum, row) => sum + row.delta, 0);

  return (
    <Commentable name="CostOfWaitingTable">
      <section className="mt-4 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
        <Kicker className="mb-1.5 block">How the cost of waiting is worked out</Kicker>
        <p className="mb-2 max-w-[90ch] text-[11.5px] leading-relaxed text-[var(--ink-secondary)]">
          Planned is the job as the agent has drafted it: labour hours at the site rate plus the
          parts, in a window the machine is already down for. Unplanned is the same failure allowed
          to happen: the downtime hours priced at the site&rsquo;s lost-production rate for that
          machine class, plus the parts the failure takes with it and the extra labour of a
          recovery. The two incidents this fortnight are the evidence the numbers are not
          optimistic — S-05 cost A$24,300 against A$4,600 planned, L-18 A$98,900 against A$4,100.
        </p>
        <div className="scrollbar-subtle overflow-x-auto">
          <table className="w-full min-w-[58rem] border-collapse text-[11.5px]">
            <thead>
              <tr className="border-b border-[var(--hairline)] text-left text-[10.5px] uppercase tracking-wide text-[var(--ink-tertiary)]">
                <th className="px-2 py-2 font-medium">Alert</th>
                <th className="px-2 py-2 font-medium">Machine</th>
                <th className="px-2 py-2 text-right font-medium">Labour</th>
                <th className="px-2 py-2 text-right font-medium">Parts planned</th>
                <th className="px-2 py-2 text-right font-medium">Planned</th>
                <th className="px-2 py-2 text-right font-medium">Down h</th>
                <th className="px-2 py-2 text-right font-medium">Lost A$/h</th>
                <th className="px-2 py-2 text-right font-medium">Unplanned</th>
                <th className="px-2 py-2 text-right font-medium">Cost of waiting</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.alertId}
                  className="border-b border-[var(--hairline)] hover:bg-[var(--surface-2)]"
                >
                  <td className="mono px-2 py-1.5">{row.alertId}</td>
                  <td className="px-2 py-1.5">
                    <Link
                      to={`/machines/${row.machine}`}
                      className="mono font-medium text-[var(--accent)] hover:underline"
                    >
                      {row.machine}
                    </Link>{' '}
                    <span className="text-[var(--ink-tertiary)]">{row.site}</span>
                  </td>
                  <td className="mono px-2 py-1.5 text-right">{row.labour}</td>
                  <td className="mono px-2 py-1.5 text-right">{fmt$(row.partsPlanned)}</td>
                  <td className="mono px-2 py-1.5 text-right font-semibold">
                    {fmt$(row.planned)}
                  </td>
                  <td className="mono px-2 py-1.5 text-right">{row.downHours}</td>
                  <td className="mono px-2 py-1.5 text-right">{groupNum(row.lostRate)}</td>
                  <td className="mono px-2 py-1.5 text-right font-semibold">
                    {fmt$(row.unplanned)}
                  </td>
                  <td
                    className="mono px-2 py-1.5 text-right font-semibold"
                    style={{ color: 'var(--crit)' }}
                  >
                    {fmt$(row.delta)}
                  </td>
                </tr>
              ))}
              <tr>
                <td colSpan={8} className="px-2 py-2 font-semibold">
                  Total exposure across the {rows.length} open alerts
                </td>
                <td
                  className="mono px-2 py-2 text-right font-semibold"
                  style={{ color: 'var(--crit)' }}
                >
                  {fmt$(total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </Commentable>
  );
}

// ---------------------------------------------------------------------------
// Fleet rules
// ---------------------------------------------------------------------------

function RulesTable({
  title,
  rows,
  footer,
}: {
  title: string;
  rows: ReturnType<typeof fleetRuleRows>;
  footer?: ReactNode;
}) {
  return (
    <Commentable name="FleetRules">
      <section className="mt-4 rounded-xl border border-[var(--hairline)] bg-[var(--surface-1)] p-4">
        <Kicker className="mb-1.5 block">{title}</Kicker>
        <div className="scrollbar-subtle overflow-x-auto">
          <table className="w-full min-w-[40rem] border-collapse text-[11.5px]">
            <thead>
              <tr className="border-b border-[var(--hairline)] text-left text-[10.5px] uppercase tracking-wide text-[var(--ink-tertiary)]">
                <th className="px-2 py-2 font-medium">Rule now running</th>
                <th className="px-2 py-2 font-medium">From</th>
                <th className="px-2 py-2 font-medium">Applies to</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.key} className="border-b border-[var(--hairline)] last:border-0">
                  <td className="px-2 py-1.5 text-[var(--ink-primary)]">{row.rule}</td>
                  <td className="mono px-2 py-1.5 text-[var(--ink-secondary)]">{row.from}</td>
                  <td className="px-2 py-1.5 text-[var(--ink-tertiary)]">{row.appliesTo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {footer ? <p className="mt-2 text-[11px] text-[var(--ink-tertiary)]">{footer}</p> : null}
      </section>
    </Commentable>
  );
}
